import { grantsCommunityMembershipEntitlement } from '../commerce/community-membership-billing';
import {
  memberTierMeetsMinimum,
  type TierAccessContext,
} from '../commerce/community-membership-tiers';
import type { CommunityMembershipContext } from '../policies/community-post.policy';

export const CreatorEventStatus = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  ARCHIVED: 'archived',
  CANCELLED: 'cancelled',
} as const;

export type CreatorEventStatusValue = (typeof CreatorEventStatus)[keyof typeof CreatorEventStatus];

export const EventAccessSource = {
  MEMBERSHIP_PERK: 'membership_perk',
  PUBLIC: 'public',
} as const;

export type EventAccessSourceValue = (typeof EventAccessSource)[keyof typeof EventAccessSource];

export {
  CreatorEventAccess,
  CreatorEventType,
  CREATOR_EVENT_ACCESS_LABELS,
  CREATOR_EVENT_ACCESS_VALUES,
  CREATOR_EVENT_TYPE_LABELS,
  CREATOR_EVENT_TYPE_VALUES,
  allowsAnonymousEventPageView,
  assertCreatorEventAccess,
  assertCreatorEventType,
  authorizeEventsManagement,
  isCreatorEventAccess,
  isCreatorEventType,
  isEventPubliclyListable,
  normalizeCreatorEventAccess,
  type CreatorEventAccessValue,
  type CreatorEventTypeValue,
} from './event-access';

export const CreatorEventRsvpStatus = {
  GOING: 'going',
  MAYBE: 'maybe',
  NOT_GOING: 'not_going',
  PENDING_APPROVAL: 'pending_approval',
  WAITLISTED: 'waitlisted',
  CANCELLED: 'cancelled',
} as const;

export type CreatorEventRsvpStatusValue =
  (typeof CreatorEventRsvpStatus)[keyof typeof CreatorEventRsvpStatus];

export type EventSatelliteRef = {
  id: string;
  slug: string | null;
  title: string;
  description?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  timezone?: string | null;
  meetingLink?: string | null;
  location?: string | null;
  href: string;
  accessSource?: EventAccessSourceValue | null;
  entitled?: boolean;
  rsvpStatus?: CreatorEventRsvpStatusValue | null;
  rsvpCount?: number;
};

export type CommunityCalendarTab = {
  visible: boolean;
  label: string;
  entitled: boolean;
  events: EventSatelliteRef[];
  message?: string | null;
};

export function isPublishedEventStatus(status: string): boolean {
  return status === CreatorEventStatus.PUBLISHED;
}

export function grantsEventAccessViaMembershipPerk(
  membership: CommunityMembershipContext | null | undefined,
  accessType: string,
  perkEnabled: boolean,
  memberTier?: TierAccessContext,
  minTier?: TierAccessContext,
): boolean {
  if (!perkEnabled) return false;
  if (!grantsCommunityMembershipEntitlement(membership, accessType)) return false;
  return memberTierMeetsMinimum(memberTier, minTier);
}

export function buildCommunityCalendarTab(params: {
  events: EventSatelliteRef[];
  membershipEntitled: boolean;
  satelliteEnabled: boolean;
  /** When true (default), only entitled events are returned (S03 exit). */
  eligibleOnly?: boolean;
}): CommunityCalendarTab {
  const eligibleOnly = params.eligibleOnly !== false;
  const mapped = params.events.map((event) => {
    const entitled =
      typeof event.entitled === 'boolean' ? event.entitled : params.membershipEntitled;
    return {
      ...event,
      entitled,
      accessSource: entitled
        ? (event.accessSource ?? EventAccessSource.MEMBERSHIP_PERK)
        : null,
    };
  });
  const events = eligibleOnly ? mapped.filter((event) => event.entitled) : mapped;
  const visible = params.satelliteEnabled && (events.length > 0 || !params.membershipEntitled);
  return {
    visible: params.satelliteEnabled && (events.length > 0 || mapped.length > 0),
    label: 'Calendar',
    entitled: params.membershipEntitled,
    events,
    message: visible
      ? params.membershipEntitled
        ? events.length
          ? null
          : 'No eligible events on this Circle calendar yet.'
        : 'Join the community to view calendar events and RSVP.'
      : null,
  };
}

export function slugifyEventTitle(title: string): string {
  const base = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return base || 'event';
}

export function sortEventsByStartAsc<T extends { startsAt?: string | Date | null }>(
  events: T[],
): T[] {
  return [...events].sort((a, b) => {
    const aTime = a.startsAt ? new Date(a.startsAt).getTime() : Number.MAX_SAFE_INTEGER;
    const bTime = b.startsAt ? new Date(b.startsAt).getTime() : Number.MAX_SAFE_INTEGER;
    return aTime - bTime;
  });
}
